import * as React from "react";
import { NavLink, generatePath, useLocation, useMatch } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import cn from "classnames";
import Styles from "./NavBar.module.scss";
import Menu from "./NavBarMenu.module.scss";
import Sticky from "../../atoms/Sticky/StickyItem.module.scss";

import { Text } from "src/components/atoms/Text/Text";
import { ROUTES } from "src/utils/constants/routes";

function NavBar() {
  const { user, logout } = useAuth0();
  const navRef = React.useRef<HTMLDivElement>(null);

  // Clears the Auth0 session (localStorage cache + Auth0 SSO cookie) and lands back on the
  // app root, which re-triggers the login flow. This is also the escape hatch when a stale
  // cached session leaves the app rendered but unable to fetch data.
  const handleLogout = () =>
    logout({ logoutParams: { returnTo: window.location.origin } });

  // Mobile-only slide-down menu (the desktop menu row is hidden on phones). Close it whenever the
  // route changes so tapping a link — or the browser back button — dismisses the panel.
  const [menuOpen, setMenuOpen] = React.useState(false);
  const location = useLocation();
  React.useEffect(() => setMenuOpen(false), [location.pathname]);

  // On an event page (any /teams/:id/schedule/:eid/* tab) swap the brand logo for a
  // "Back to Schedule" link so users can jump straight back to the team's schedule
  // instead of hitting the browser back button or going all the way Home. The Live
  // page is excluded — it can be viewed unauthenticated (magic link), so its schedule
  // would be auth-gated.
  const eventMatch = useMatch("/teams/:id/schedule/:eid/*");
  const onEventPage = eventMatch !== null && eventMatch.params["*"] !== "live";
  const schedulePath =
    onEventPage && eventMatch?.params.id
      ? generatePath(ROUTES.teams.events.schedule, { id: eventMatch.params.id })
      : null;

  // Publish the global nav's real height as --top so everything that sticks
  // below it (the team header, schedule month dividers, stats grid header — all
  // keyed off --top) pins just under it regardless of its actual height. The
  // nav varies by breakpoint and by content (logo only vs logo + email menu),
  // so a hardcoded --top let the team header slide up under the nav on mobile.
  React.useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const publishHeight = () => document.documentElement.style.setProperty("--top", `${el.offsetHeight}px`);
    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={navRef} className={cn(Styles.stickyItem, Sticky.stickyItem)} style={{ top: "0px" }}>
      <div className={cn(Styles.navBarContainer, Styles.hideBoxShadow)}>
        <div className={Styles.mainContainer}>
          {schedulePath ? (
            <NavLink to={schedulePath} className={Styles.backToSchedule} data-testid="nav-back-to-schedule">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19 12H5M5 12L12 19M5 12L12 5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Back to Schedule
            </NavLink>
          ) : (
            <NavLink to="/teams" target="_self" rel="noreferrer noopener" title="GC" className={Styles.logoHolder}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 225 21" fill="none" data-testid="gc-logo">
              <path
                fill="#00F092"
                fillRule="evenodd"
                d="M28.759 16.18c-.807 0-1.582-.193-2.324-.516a5.235 5.235 0 0 1-1.84-1.42 5.81 5.81 0 0 1-1.097-2.066 5.869 5.869 0 0 1-.162-2.485c.323-2.26 2.034-4.164 4.26-4.712.13-.032.872-.162 1.84-.162h6.133c.032 0 .84 0 1.614-.355 1.226-.677 1.969-2 1.969-3.389V.946H28.759c-4.325 0-8.102 3.002-9.134 7.262v.097h-5.713c-2.066 0-3.744 1.646-3.841 3.744v.13h9.07c0 .064-.033.128-.065.225-.613 1.872-2.227 3.325-4.164 3.809 0 0-.807.194-1.872.161h-2.904c-.969 0-1.711-.129-1.84-.161C5.81 15.6 4.036 13.34 3.97 10.79c0-.29 0-.677.032-1.032.323-2.292 2.033-4.196 4.26-4.745.13-.032.872-.161 1.84-.161h3.131c.032 0 1.097 0 1.904-.517a3.907 3.907 0 0 0 1.679-3.227V.946H9.393C4.326.946.098 5.142.001 10.34a9.43 9.43 0 0 0 2.711 6.778c1.807 1.872 4.325 2.937 6.875 2.937h4.293c2.904 0 5.68-1.388 7.455-3.712 1.84 2.324 4.648 3.712 7.585 3.712h6.423c2.066 0 3.744-1.646 3.841-3.744v-.13H28.759Z"
                clipRule="evenodd"
              ></path>
              <path
                fill="#ffffff"
                fillRule="evenodd"
                d="M153.178 11.967h-3.034l2.55-4.841 2.582 4.841h-2.098Zm-1.743-7.52-6.39 12.103h2.679l1.162-2.162h7.681l1.162 2.162h2.679l-6.423-12.103h-2.55ZM172.608 13.032l-.032-.032-7.714-8.553h-2.743V16.55h2.388V7.513l.032.032 8.102 9.005h2.356V4.447h-2.389v8.585ZM219.312 10.257h-6.036V6.835h6.036c.452 0 .871.194 1.194.517.323.323.484.742.484 1.194 0 .452-.161.871-.484 1.194a1.567 1.567 0 0 1-1.194.517Zm1.42 2.162.161-.065a4.003 4.003 0 0 0 1.775-1.484 4.17 4.17 0 0 0 .71-2.292c0-1.097-.419-2.13-1.194-2.905a3.904 3.904 0 0 0-2.872-1.226h-8.392V16.55h2.356v-3.905h4.486l3.389 3.905h3.164l-3.583-4.13ZM140.978 9.288h-8.037V4.447h-2.389V16.55h2.389V11.71h8.037v4.841h2.356V4.447h-2.356v4.841ZM95.404 4.447h-1.13l-3.84 8.779-3.874-8.78h-3.389V16.55h2.42V7.707l3.874 8.843h1.904l3.906-8.875v8.875h2.388V4.447h-2.26ZM74.102 11.97h-3.034l2.55-4.841L76.2 11.97h-2.098Zm-1.743-7.52-6.39 12.103h2.678l1.162-2.162h7.682l1.162 2.162h2.679L74.909 4.45h-2.55ZM56.866 11.324v.13h6.068v.032c-.451 1.549-1.872 2.646-3.485 2.646H56.22c-1.969 0-3.583-1.613-3.615-3.582a3.504 3.504 0 0 1 1.033-2.582c.71-.71 1.678-1.13 2.646-1.13h2.938c1.29 0 2.356-.968 2.517-2.26V4.45H56.19c-1.614 0-3.131.645-4.293 1.807a6.018 6.018 0 0 0-1.71 4.325c.064 3.292 2.775 5.971 6.1 5.971h3.098c3.26-.032 5.971-2.711 6.036-5.971V9.065h-6.036a2.522 2.522 0 0 0-2.518 2.26ZM184.421 11.322v.129h6.068v.032c-.452 1.55-1.872 2.647-3.486 2.647h-3.227c-1.969 0-3.583-1.614-3.615-3.583a3.505 3.505 0 0 1 1.033-2.582c.71-.71 1.678-1.13 2.646-1.13h2.938c1.291 0 2.356-.968 2.517-2.259v-.13h-5.519c-1.614 0-3.131.646-4.293 1.808a6.184 6.184 0 0 0-1.743 4.325c.065 3.292 2.776 5.971 6.1 5.971h3.099c3.292-.032 6.003-2.71 6.036-5.938V9.095h-6.036c-1.323 0-2.389.968-2.518 2.227ZM121.031 6.835h4.422c1.291 0 2.356-.968 2.518-2.259v-.13h-6.94c-1.613 0-3.13.646-4.292 1.808a6.184 6.184 0 0 0-1.743 4.325c.064 3.292 2.776 5.971 6.1 5.971h4.325c1.291 0 2.356-.968 2.518-2.259v-.129h-6.875a3.686 3.686 0 0 1-2.647-1.13c-.678-.71-1.065-1.614-1.033-2.582.065-2 1.679-3.615 3.647-3.615ZM111.058 6.835a2.49 2.49 0 0 0 2.485-2.259v-.13h-12.459V16.55h9.974a2.49 2.49 0 0 0 2.485-2.259v-.129H103.44v-2.453h4.519c1.227 0 2.259-1 2.259-2.26v-.16h-6.745V6.834h7.585ZM205.724 6.835a2.49 2.49 0 0 0 2.485-2.259v-.13H195.75V16.55h9.974a2.49 2.49 0 0 0 2.485-2.259v-.129h-10.102v-2.453h4.486c1.227 0 2.259-1 2.259-2.26v-.16h-6.745V6.834h7.617ZM227.477 16.55h-.322v-1.743h-.613v-.29h1.516v.29h-.581v1.743ZM229.317 16.55l-.581-1.678c0 .258.033.484.033.71v.968h-.291v-2.033h.452l.549 1.614.581-1.614h.452v2.033h-.323v-1.678l-.613 1.678h-.259Z"
                clipRule="evenodd"
              ></path>
            </svg>
            </NavLink>
          )}
          {/* The full nav menu is hidden on phones, so surface a hamburger in the always-visible
              banner row that opens a slide-down menu. Hidden on desktop, where the menu row shows. */}
          <button
            type="button"
            className={Styles.hamburger}
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav-menu"
            data-testid="nav-hamburger"
          >
            {menuOpen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M6 6l12 12M18 6L6 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M3 6h18M3 12h18M3 18h18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            )}
          </button>
        </div>
        <div className={Menu["nav-menu-container"]}>
          <NavLink to="/teams" data-testid="nav-home-link">
            <Text className={Menu["navMenu__item"]} color="white" size="small" weight="semibold">
              Home
            </Text>
          </NavLink>
          <NavLink to={ROUTES.live} data-testid="nav-live-link" className={Styles.navLive}>
            <span className={Styles.navLiveDot} aria-hidden="true" />
            <Text className={Menu["navMenu__item"]} color="white" size="small" weight="semibold">
              Live
            </Text>
          </NavLink>
          <NavLink to={ROUTES.players} data-testid="nav-players-link">
            <Text className={Menu["navMenu__item"]} color="white" size="small" weight="semibold">
              Players
            </Text>
          </NavLink>
          <div className={Menu.menuEmail}>
            {user && (
              <NavLink to="/profile">
                <Text className={Styles["navMenu__item"]} color="white" size="small" weight="semibold">
                  {user.email}
                </Text>
              </NavLink>
            )}
            {/* Rendered even when `user` is missing so a broken/stale session still has a way out. */}
            <button type="button" className={Styles.logoutButton} onClick={handleLogout} data-testid="nav-logout">
              <Text className={Menu["navMenu__item"]} color="white" size="small" weight="semibold">
                Log Out
              </Text>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile slide-down menu (phones only — the desktop menu row above is hidden there).
          Rendered only when open and positioned absolutely under the sticky banner, so it
          overlays the page without affecting the published --top height. */}
      {menuOpen && (
        <>
          <div className={Styles.mobileScrim} onClick={() => setMenuOpen(false)} aria-hidden="true" />
          <div id="mobile-nav-menu" className={Styles.mobileMenu} onClick={() => setMenuOpen(false)}>
            <NavLink to="/teams" className={Styles.mobileMenuItem} data-testid="mobile-nav-home">
              <Text color="white" weight="semibold">
                Home
              </Text>
            </NavLink>
            <NavLink to={ROUTES.live} className={Styles.mobileMenuItem} data-testid="mobile-nav-live">
              <span className={Styles.navLiveDot} aria-hidden="true" />
              <Text color="white" weight="semibold">
                Live
              </Text>
            </NavLink>
            <NavLink to={ROUTES.players} className={Styles.mobileMenuItem} data-testid="mobile-nav-players">
              <Text color="white" weight="semibold">
                Players
              </Text>
            </NavLink>
            <NavLink to={ROUTES.profile} className={Styles.mobileMenuItem} data-testid="mobile-nav-profile">
              <Text color="white" weight="semibold">
                Profile
              </Text>
              {user?.email && (
                <Text color="white" size="small" className={Styles.mobileMenuEmail}>
                  {user.email}
                </Text>
              )}
            </NavLink>
            <button
              type="button"
              className={cn(Styles.mobileMenuItem, Styles.mobileMenuButton)}
              onClick={handleLogout}
              data-testid="mobile-nav-logout"
            >
              <Text color="white" weight="semibold">
                Log Out
              </Text>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
export default NavBar;
